import type { DataOption } from '@/common.types'
import type { MinimalStepRuleModel } from '@/shared/generated'
import type { JSONSchemaType } from 'ajv/dist/core'
import type { TFunction } from 'i18next'
import type { MouseEvent } from 'react'

import React, { useEffect, useMemo, useState } from 'react'
import { sortBy } from 'lodash/fp'
import { PlusIcon, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from '@/shared/components/ui/input-group'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/shared/components/ui/table'
import { ampli } from '@/shared/lib/ampli'
import { numericOnlyPositive } from '@/shared/tools/constants'

const getValueTypes = (t: TFunction): DataOption[] => [
	{
		label: t('relative'),
		value: 'relative',
	},
	{
		label: t('absolute'),
		value: 'absolute',
	},
]
const opozitProp = (prop: string) =>
	prop === 'min_step_relative' ? 'min_step_absolute' : 'min_step_relative'

/**
 * Return first not empty type or first type if all empty
 * @param item MinimalStepRuleModel
 * @param options Option[]
 * @returns Option | null
 */
const getInitStepValueType = (
	item: MinimalStepRuleModel,
	options: DataOption[]
): DataOption | undefined => {
	const types = Object.entries(item).filter(([key]) => key !== 'min_price')
	const notEmptyType = types.find(([key, value]) => key && value !== 0)
	if (notEmptyType) {
		return options.find(({ value }) => `min_step_${value}` === notEmptyType[0])
	} else {
		return options.find(({ value }) => `min_step_${value}` === types[0][0])
	}
}

type StepItemProps = {
	isRemovable: boolean
	item: MinimalStepRuleModel
	index: number
	max: number | string
	onRemoveClick(index: number): void
	onChange(index: number, name: string, value: string): void
	onBlur(): void
}

function StepItem({
	isRemovable,
	item,
	index,
	max,
	onBlur,
	onRemoveClick,
	onChange,
}: StepItemProps) {
	const { t } = useTranslation()
	const valueTypesOptions = useMemo(() => getValueTypes(t), [t])
	const [stepValueType, setStepValueType] = useState<DataOption>(
		() => getInitStepValueType(item, valueTypesOptions) ?? valueTypesOptions[0]
	)
	useEffect(() => {
		setStepValueType(
			getInitStepValueType(item, valueTypesOptions) ?? valueTypesOptions[0]
		)
	}, [item])

	const stepValuePropName = `min_step_${stepValueType.value}`
	const stepValue = item?.[stepValuePropName as keyof MinimalStepRuleModel]

	const _onRemoveClick = () => onRemoveClick(index)
	const _onBlur = () => onBlur()
	const _onMinPriceChange = (event: React.ChangeEvent<HTMLInputElement>) =>
		onChange(index, 'min_price', event.target.value)
	const _onMaxPriceChange = (event: React.ChangeEvent<HTMLInputElement>) =>
		onChange(index + 1, 'min_price', event.target.value)
	const _onStepChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		onChange(index, stepValuePropName, event.target.value)
	}
	const _onValueTypeChange = (value: string) => {
		if (!value) return
		const stepValuePropName = `min_step_${value}`
		onChange(
			index,
			stepValuePropName,
			String(item?.[opozitProp(stepValuePropName)] ?? '')
		)
		//setStepValueType(stepValueType)
	}

	return (
		<TableRow data-testid={`pc-min-price-step-row-${index}`}>
			<TableCell className='border-none p-2'>
				<div className='flex items-center'>
					<div className='mb-0' data-testid='pc-min-price-step-rule-min'>
						<Input
							name='min_price'
							value={item.min_price}
							onBlur={_onBlur}
							disabled={index === 0}
							invalid={!('' + item.min_price).length}
							placeholder={t('empty')}
							onChange={_onMinPriceChange}
						/>
					</div>
					<span className='mx-2.5'>-</span>
					<div className='mb-0' data-testid='pc-min-price-step-rule-max'>
						<Input value={max} disabled onChange={_onMaxPriceChange} />
					</div>
				</div>
			</TableCell>
			<TableCell className='border-none p-2'>
				<div className='flex items-center'>
					<div
						className='mr-2.5 mb-0 w-64'
						data-testid='pc-min-price-step-rule-value-type'
					>
						<Select
							value={stepValueType.value}
							onValueChange={_onValueTypeChange}
						>
							<SelectTrigger className='w-full' name='type' data-name='type'>
								<SelectValue
									placeholder={t('general_select', {
										defaultValue: 'Select',
									})}
								/>
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									{valueTypesOptions.map((choice) => (
										<SelectItem
											key={choice.value}
											value={choice.value}
											data-value={choice.value}
										>
											{choice.label}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
					</div>
					<div className='mb-0' data-testid='pc-min-price-step-rule-value'>
						<InputGroup>
							<InputGroupInput
								name='min_step_value'
								value={stepValue}
								aria-invalid={!stepValue && stepValue !== 0}
								onChange={_onStepChange}
							/>
							<InputGroupAddon align='inline-end'>
								{stepValueType.value === 'absolute' ? '#' : '%'}
							</InputGroupAddon>
						</InputGroup>
					</div>
					{isRemovable ? (
						<Button
							variant='ghost'
							size='icon'
							className='ml-auto'
							onClick={_onRemoveClick}
							data-testid='pc-min-price-step-rule-remove'
						>
							<XIcon />
						</Button>
					) : (
						<div className='w-8 shrink-0' />
					)}
				</div>
			</TableCell>
		</TableRow>
	)
}
export const MinimalPriceStepFileds = (props: {
	formData: MinimalStepRuleModel[]
	onChange: (data: MinimalStepRuleModel[]) => void
	schema: JSONSchemaType<MinimalStepRuleModel[]>
}) => {
	const { t } = useTranslation()
	const { formData, onChange, schema } = props
	const steps: MinimalStepRuleModel[] = formData || schema.default || []
	const handleChange = (index: number, name: string, value: string) => {
		const maxValue = Number(value.replace(numericOnlyPositive, ''))
		const data = steps.map((item, i) =>
			index === i
				? ({
						min_price: item.min_price,
						// Params order is important for correct work
						[name]: maxValue < 0 ? 1 : maxValue,
						[opozitProp(name)]: 0,
					} as unknown as MinimalStepRuleModel)
				: item
		)
		onChange(data)
	}
	const handleAddStep = (e: MouseEvent<HTMLButtonElement>) => {
		e.preventDefault()
		ampli.settingsPricingCampaignsAddAMinimalPriceChange()
		const prevStep = steps[steps.length - 1]
		const newItem: MinimalStepRuleModel = {
			min_price: +prevStep.min_price + 500,
			min_step_absolute: prevStep.min_step_absolute || 0,
			min_step_relative: prevStep.min_step_relative || 0,
		}
		onChange([...steps, newItem])
	}
	const handleRemoveStep = (index: number) => {
		ampli.settingsPricingCampaignsDeleteAMinimalPriceChange()
		onChange(steps.filter((item, i) => i !== index))
	}
	const onBlur = () => onChange(sortBy('min_price', [...steps]))

	return (
		<>
			<div className='mb-2 w-full border-b border-solid'>
				<Table>
					<TableHeader className='top-auto'>
						<TableRow>
							<TableHead className='w-1/3 bg-transparent p-2 text-xs'>
								{t('general_range')}
							</TableHead>
							<TableHead className='w-2/3 bg-transparent p-2 text-xs'>
								{t('value')}
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{steps?.length > 0 ? (
							steps.map((item: MinimalStepRuleModel, index: number) => {
								const max = steps[index + 1]
									? steps[index + 1].min_price - 1
									: '∞'

								return (
									<StepItem
										key={index}
										max={max}
										onBlur={onBlur}
										isRemovable={steps.length > 1 && index > 0}
										onRemoveClick={handleRemoveStep}
										onChange={handleChange}
										item={item}
										index={index}
									/>
								)
							})
						) : (
							<TableRow>
								<TableCell colSpan={2} className='border-none'>
									<div className='p-5 text-center text-sm text-muted-foreground'>
										{t('general_no_data')}
									</div>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			<Button
				disabled={steps.length >= 15}
				variant='ghost'
				data-testid='pc-min-price-step-add-rule'
				onClick={handleAddStep}
			>
				<PlusIcon />
				{t('optimization_groups_settings_add_price', {
					defaultValue: 'Add price',
				})}
			</Button>
		</>
	)
}
